import React from "react";
import {
  Avatar,
  createTheme,
  Box,
  Card,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Button,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  MenuItem,
  Stack,
  Tooltip,
  Collapse,
  IconButton,
} from "@mui/material";

import Client from "../Client";
import Config from "../ClientConfig";
import { locationsList } from "./simpleBiReportsApi";

import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";

let theme = createTheme();

const objectToCsv = function (data) {
  const csvRows = [];
  if (!data || data.length === 0) return "";

  const headers = Object.keys(data[0]);
  csvRows.push(headers.join(","));

  for (const row of data) {
    const values = headers.map((header) => {
      const val = row[header] ?? "";
      return `"${String(val).replaceAll('"', '""')}"`;
    });
    csvRows.push(values.join(","));
  }

  return csvRows.join("\n");
};

class OrderFeedbackView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client("none"),

      page: 0,
      rowsPerPage: 25,

      loading: false,
      error: "",

      period: "last7",
      dateFrom: "",
      dateTo: "",
      score: "",
      locationId: "",
      locationName: { name: "All Locations" },
      locations: [],

      feedbackList: [],
      summary: { metrics: { total: 0, avg_score: null }, by_score: [] },

      filterData: {},
      showFilters: false,
    };

    this.getData = this.getData.bind(this);
    this.getLocations = this.getLocations.bind(this);
    this.handleChangePage = this.handleChangePage.bind(this);
    this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
    this.onDownload = this.onDownload.bind(this);
    this.applyPeriod = this.applyPeriod.bind(this);
    this.selectLocation = this.selectLocation.bind(this);

    this.getLocationNameById = this.getLocationNameById.bind(this);
    this.getPeriodLabel = this.getPeriodLabel.bind(this);
    this.renderAppliedFilters = this.renderAppliedFilters.bind(this);
  }

  componentDidMount() {
    const now = new Date();
    const from = this.startOfDay(this.addDays(now, -6));
    const to = this.endOfDay(now);

    this.setState(
      {
        dateFrom: this.toLocalInputValue(from),
        dateTo: this.toLocalInputValue(to),
      },
      () => {
        this.getLocations();
        this.getData();
      }
    );
  }

  getLocations() {
    const resp = locationsList(this.state.client);

    const locations = Array.isArray(resp?.locations) ? resp.locations : [];

    if (locations.length === 1) {
      this.setState(
        {
          locations,
          locationId: String(locations[0].iref),
          locationName: locations[0],
        },
        this.getData
      );
    } else {
      this.setState({ locations });
    }
  }

  pad2(n) {
    return String(n).padStart(2, "0");
  }

  toLocalInputValue(d) {
    const yyyy = d.getFullYear();
    const mm = this.pad2(d.getMonth() + 1);
    const dd = this.pad2(d.getDate());
    const hh = this.pad2(d.getHours());
    const mi = this.pad2(d.getMinutes());

    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  }

  fromLocalInputValue(s) {
    if (!s) return null;

    const [datePart, timePart] = s.split("T");
    if (!datePart || !timePart) return null;

    const [y, m, d] = datePart.split("-").map((x) => parseInt(x, 10));
    const [hh, mi] = timePart.split(":").map((x) => parseInt(x, 10));

    return new Date(y, (m || 1) - 1, d || 1, hh || 0, mi || 0, 0, 0);
  }

  startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  endOfDay(d) {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  }

  addDays(d, days) {
    const x = new Date(d);
    x.setDate(x.getDate() + days);
    return x;
  }

  startOfMonth(d) {
    const x = new Date(d);
    x.setDate(1);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  endOfMonth(d) {
    const x = new Date(d);
    x.setMonth(x.getMonth() + 1, 1);
    x.setHours(0, 0, 0, 0);
    x.setMilliseconds(-1);
    return x;
  }

  applyPeriod(period) {
    const now = new Date();

    if (period === "today") {
      this.setState({
        period,
        dateFrom: this.toLocalInputValue(this.startOfDay(now)),
        dateTo: this.toLocalInputValue(this.endOfDay(now)),
        page: 0,
      });
      return;
    }

    if (period === "yesterday") {
      const y = this.addDays(now, -1);

      this.setState({
        period,
        dateFrom: this.toLocalInputValue(this.startOfDay(y)),
        dateTo: this.toLocalInputValue(this.endOfDay(y)),
        page: 0,
      });
      return;
    }

    if (period === "last7") {
      this.setState({
        period,
        dateFrom: this.toLocalInputValue(this.startOfDay(this.addDays(now, -6))),
        dateTo: this.toLocalInputValue(this.endOfDay(now)),
        page: 0,
      });
      return;
    }

    if (period === "thisMonth") {
      this.setState({
        period,
        dateFrom: this.toLocalInputValue(this.startOfMonth(now)),
        dateTo: this.toLocalInputValue(this.endOfDay(now)),
        page: 0,
      });
      return;
    }

    if (period === "lastMonth") {
      const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);

      this.setState({
        period,
        dateFrom: this.toLocalInputValue(this.startOfMonth(lm)),
        dateTo: this.toLocalInputValue(this.endOfMonth(lm)),
        page: 0,
      });
      return;
    }

    this.setState({ period, page: 0 });
  }

  buildQueryData() {
    const queryData = {};
    const from = this.fromLocalInputValue(this.state.dateFrom);
    const to = this.fromLocalInputValue(this.state.dateTo);

    if (from) queryData["createdOn__gte"] = from.toISOString();
    if (to) queryData["createdOn__lte"] = to.toISOString();

    if (this.state.score !== "" && this.state.score !== null) {
      queryData["score"] = this.state.score;
    }

    if (this.state.locationId && this.state.locationId !== "") {
      queryData["location"] = this.state.locationId;
    }

    return queryData;
  }

  getData() {
    const queryData = this.buildQueryData();

    this.setState({ loading: true, error: "" }, () => {
      try {
        const list = this.state.client.getData(
          Config.API_URL_ORDERS_FEEDBACK,
          queryData
        );

        const summary = this.state.client.getData(
          Config.API_URL_ORDERS_FEEDBACK_SUMMARY,
          queryData
        );

        this.setState({
          feedbackList: Array.isArray(list) ? list : [],
          summary: summary || { metrics: { total: 0, avg_score: null }, by_score: [] },
          loading: false,
          filterData: queryData,
        });
      } catch (e) {
        this.setState({
          loading: false,
          error: e?.message ? String(e.message) : "Failed to load feedback",
        });
      }
    });
  }

  selectLocation = (event) => {
    const value = event.target.value;

    const locationObj = this.state.locations.find(
      (o) => String(o.iref) === String(value)
    );

    this.setState({
      locationId: value,
      locationName: locationObj || { name: "All Locations" },
      page: 0,
    });
  };

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({
      rowsPerPage: +event.target.value,
      page: 0,
    });
  };

  onDownload() {
    const rows = (this.state.feedbackList || []).map((x) => ({
      iref: x.iref,
      order_id: x.order_id,
      order_createdOn: x.order_createdOn,
      score: x.score,
      details: x.details,
      order_location: x.order_location,
    }));

    const csvData = rows.length > 0 ? objectToCsv(rows) : "";
    const from = this.state.filterData?.createdOn__gte ?? "";
    const to = this.state.filterData?.createdOn__lte ?? "";
    const score = this.state.filterData?.score ?? "";
    const location = this.state.filterData?.location ?? "";

    const header =
      `Feedback export\n` +
      `From: ${from}\n` +
      `To: ${to}\n` +
      `Score filter: ${score}\n` +
      `Location filter: ${location}\n` +
      `Total: ${this.state.summary?.metrics?.total ?? 0}\n` +
      `Avg score: ${this.state.summary?.metrics?.avg_score ?? ""}\n`;

    const csv = "\uFEFF" + header + "\n" + csvData;

    const link = document.createElement("a");
    link.download = `order_feedback_${new Date().toISOString().slice(0, 10)}.csv`;
    link.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    link.click();
  }

  getPeriodLabel(period) {
    switch (period) {
      case "today":
        return "Today";
      case "yesterday":
        return "Yesterday";
      case "last7":
        return "Last 7 days";
      case "thisMonth":
        return "This Month";
      case "lastMonth":
        return "Last Month";
      case "custom":
        return "Custom";
      default:
        return String(period || "");
    }
  }

  getLocationNameById(id) {
    if (!id) return "";
    const loc = (this.state.locations || []).find((x) => String(x.iref) === String(id));
    return loc?.name || String(id);
  }

  getAvailableLocations() {
    return (this.state.locations || []).map((l) => ({
      iref: l.iref,
      name: l.name,
    }));
  }

  renderAppliedFilters() {
    const score = this.state.filterData?.score ?? "";
    const locationId = this.state.filterData?.location ?? "";
    const locationName = locationId ? this.getLocationNameById(locationId) : "All Locations";

    return (
      <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.75 }}>
        <Chip
          size="small"
          variant="outlined"
          label={`Period: ${this.getPeriodLabel(this.state.period)}`}
          sx={this.chipSx()}
        />

        {locationId ? (
          <Chip
            size="small"
            variant="outlined"
            label={`Location: ${locationName}`}
            sx={this.chipSx()}
          />
        ) : null}

        {score ? (
          <Chip
            size="small"
            variant="outlined"
            label={`Score: ${score}`}
            sx={this.chipSx()}
            onDelete={() => this.setState({ score: "" }, this.getData)}
          />
        ) : null}
      </Stack>
    );
  }

  renderHeader() {
    return (
      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          py: { xs: 0.75, sm: 1 },
          borderBottom: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: "#1a237e",
                fontSize: { xs: "13px", sm: "14px" },
                fontWeight: 800,
                letterSpacing: "0.4px",
                textTransform: "uppercase",
                lineHeight: 1.2,
              }}
            >
              Order Feedback
            </Typography>

            {this.renderAppliedFilters()}
          </Box>

          <Stack direction="row" spacing={0.75} alignItems="center">
            {this.state.loading ? <CircularProgress size={18} /> : null}

            <Tooltip title={this.state.showFilters ? "Hide filters" : "Show filters"}>
              <IconButton
                size="small"
                onClick={() => this.setState({ showFilters: !this.state.showFilters })}
                sx={{
                  borderRadius: "10px",
                  border: "1px solid rgba(0,0,0,0.15)",
                  width: 34,
                  height: 34,
                }}
              >
                <FilterAltIcon fontSize="small" />
                {this.state.showFilters ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title="Search">
              <span>
                <Button
                  color="inherit"
                  size="small"
                  onClick={this.getData}
                  variant="contained"
                  disabled={this.state.loading}
                  sx={{
                    maxWidth: 34,
                    minWidth: 34,
                    maxHeight: 34,
                    minHeight: 34,
                  }}
                >
                  <Avatar sx={{ width: 22, height: 22 }}>
                    <SearchIcon fontSize="small" />
                  </Avatar>
                </Button>
              </span>
            </Tooltip>

            <Tooltip title="Download CSV">
              <span>
                <Button
                  color="inherit"
                  size="small"
                  onClick={this.onDownload}
                  variant="contained"
                  disabled={this.state.loading || (this.state.feedbackList || []).length === 0}
                  sx={{
                    maxWidth: 34,
                    minWidth: 34,
                    maxHeight: 34,
                    minHeight: 34,
                    display: { xs: "none", md: "inline-flex" },
                  }}
                >
                  <Avatar sx={{ width: 22, height: 22, bgcolor: theme.palette.success.light }}>
                    <FileDownloadIcon fontSize="small" />
                  </Avatar>
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
    );
  }

  renderFiltersPanel() {
    const availableLocations = this.getAvailableLocations();
    const showLocationSelect = availableLocations.length > 1;
    const singleLocationName =
      availableLocations[0]?.name || this.state.locationName?.name || "Location";

    return (
      <Collapse in={this.state.showFilters} timeout="auto" unmountOnExit>
        <Box
          sx={{
            px: { xs: 1, sm: 1.5 },
            py: { xs: 0.75, sm: 1 },
            borderBottom: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr 1fr",
                sm: showLocationSelect
                  ? "150px minmax(180px, 240px) 110px auto"
                  : "150px minmax(160px, 220px) 110px auto",
              },
              gap: {
                xs: 0.85,
                sm: 1,
              },
              alignItems: "end",
            }}
          >
            <TextField
              select
              label="Period"
              size="small"
              variant="outlined"
              value={this.state.period}
              onChange={(e) => this.applyPeriod(e.target.value)}
              sx={this.compactFilterSx()}
            >
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="last7">Last 7 days</MenuItem>
              <MenuItem value="thisMonth">This Month</MenuItem>
              <MenuItem value="lastMonth">Last Month</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>

            {showLocationSelect ? (
              <TextField
                select
                label="Location"
                size="small"
                variant="outlined"
                value={this.state.locationId}
                onChange={this.selectLocation}
                sx={this.compactFilterSx()}
              >
                <MenuItem value="">All</MenuItem>
                {availableLocations.map((l) => (
                  <MenuItem key={l.iref} value={String(l.iref)}>
                    {l.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Box
                sx={{
                  minHeight: 40,
                  px: 1.25,
                  py: 0.55,
                  border: "1px solid rgba(0,0,0,0.18)",
                  borderRadius: "10px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.55)",
                }}
              >
                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: "11px",
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  Location
                </Typography>

                <Typography
                  sx={{
                    color: "#1e2833",
                    fontSize: {
                      xs: "13px",
                      sm: "13.5px",
                    },
                    fontWeight: 800,
                    lineHeight: 1.25,
                    mt: 0.35,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {singleLocationName}
                </Typography>
              </Box>
            )}

            <TextField
              select
              label="Score"
              size="small"
              variant="outlined"
              value={this.state.score}
              onChange={(e) => this.setState({ score: e.target.value, page: 0 })}
              sx={this.compactFilterSx()}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="1">1 ★</MenuItem>
              <MenuItem value="2">2 ★</MenuItem>
              <MenuItem value="3">3 ★</MenuItem>
              <MenuItem value="4">4 ★</MenuItem>
              <MenuItem value="5">5 ★</MenuItem>
            </TextField>

            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{
                gridColumn: {
                  xs: "1 / -1",
                  sm: "auto",
                },
                justifyContent: {
                  xs: "flex-end",
                  sm: "flex-start",
                },
                minHeight: 40,
              }}
            >
              <Button
                size="small"
                variant="contained"
                onClick={this.getData}
                disabled={this.state.loading}
                sx={{
                  minWidth: {
                    xs: 96,
                    sm: 88,
                  },
                  height: 36,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 800,
                }}
              >
                Search
              </Button>

              <Tooltip title="Clear filters">
                <span>
                  <IconButton
                    size="small"
                    onClick={() =>
                      this.setState(
                        {
                          score: "",
                          locationId: showLocationSelect ? "" : this.state.locationId,
                          page: 0,
                        },
                        this.getData
                      )
                    }
                    disabled={
                      this.state.loading ||
                      (this.state.score === "" &&
                        (showLocationSelect ? this.state.locationId === "" : true))
                    }
                    sx={{
                      width: 36,
                      height: 36,
                      border: "1px solid rgba(0,0,0,0.14)",
                      borderRadius: "10px",
                      backgroundColor: "rgba(255,255,255,0.55)",
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>

          {this.state.period === "custom" && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", md: "220px 220px auto" },
                gap: { xs: 1, sm: 1.25 },
                mt: 1,
              }}
            >
              <TextField
                label="From"
                size="small"
                type="datetime-local"
                variant="standard"
                value={this.state.dateFrom}
                onChange={(e) =>
                  this.setState({ period: "custom", dateFrom: e.target.value, page: 0 })
                }
                InputLabelProps={{ shrink: true }}
                sx={this.filterSx()}
              />

              <TextField
                label="To"
                size="small"
                type="datetime-local"
                variant="standard"
                value={this.state.dateTo}
                onChange={(e) =>
                  this.setState({ period: "custom", dateTo: e.target.value, page: 0 })
                }
                InputLabelProps={{ shrink: true }}
                sx={this.filterSx()}
              />
            </Box>
          )}
        </Box>
      </Collapse>
    );
  }

  renderCompactSummary() {
    const total = this.state.summary?.metrics?.total ?? 0;
    const avg = this.state.summary?.metrics?.avg_score;
    const byScore = this.state.summary?.by_score ?? [];

    return (
      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          py: { xs: 0.75, sm: 1 },
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          backgroundColor: "#f6f4ef",
        }}
      >
        <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center">
          <Chip variant="outlined" label={`Total: ${total}`} sx={this.chipSx()} />
          <Chip
            variant="outlined"
            label={`Avg: ${avg == null ? "-" : Number(avg).toFixed(2)}`}
            sx={this.chipSx()}
          />

          {byScore.length === 0 ? (
            <Chip size="small" label="No data" sx={this.chipSx()} />
          ) : (
            byScore.map((x) => (
              <Chip
                key={`dist-${x.score}`}
                size="small"
                variant="outlined"
                label={`${x.score}: ${x.count}`}
                onClick={() => this.setState({ score: String(x.score), page: 0 }, this.getData)}
                sx={{
                  ...this.chipSx(),
                  cursor: "pointer",
                }}
              />
            ))
          )}
        </Stack>
      </Box>
    );
  }

  renderFeedbackTable() {
    const page = this.state.page;
    const rowsPerPage = this.state.rowsPerPage;

    const rows = (this.state.feedbackList || []).map((x, idx) => ({
      id: x.iref ?? idx + 1,
      iref: x.iref,
      order_id: x.order_id,
      createdOn: x.order_createdOn,
      score: x.score,
      details: String(x.details || "")
        .replace(/\r\n/g, "\n")
        .replace(/\n\s*\n+/g, "\n")
        .trim(),
      order_location: x.order_location,
    }));

    const paged =
      rowsPerPage > 0
        ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : rows;

    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            {paged.map((r) => (
              <Box
                key={r.id}
                sx={{
                  p: 1,
                  borderBottom: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      Order #{r.order_id}
                    </Typography>
                  </Box>

                  <Chip size="small" variant="outlined" label={r.score ?? 0} sx={this.chipSx()} />
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  {r.createdOn ? new Date(r.createdOn).toLocaleString("ro-RO") : ""}
                </Typography>

                {r.order_location ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {r.order_location}
                  </Typography>
                ) : null}

                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.75,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    lineHeight: 1.35,
                  }}
                >
                  {r.details || "-"}
                </Typography>
              </Box>
            ))}

            {paged.length === 0 && !this.state.loading ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ opacity: 0.75 }}>
                  No feedback found for selected filters.
                </Typography>
              </Box>
            ) : null}
          </Box>

          <TableContainer sx={{ display: { xs: "none", md: "block" }, height: "100%" }}>
            <Table stickyHeader aria-label="feedback table" size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={this.headerCellSx("center", 90)}><b>Order</b></TableCell>
                  <TableCell sx={this.headerCellSx("left", 165)}><b>Created</b></TableCell>
                  <TableCell sx={this.headerCellSx("center", 80)}><b>Score</b></TableCell>
                  <TableCell sx={this.headerCellSx("left", 150)}><b>Location</b></TableCell>
                  <TableCell sx={this.headerCellSx("left")}><b>Details</b></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paged.map((r) => (
                  <TableRow hover key={r.id} tabIndex={-1}>
                    <TableCell sx={this.bodyCellSx("center")}>{r.order_id}</TableCell>

                    <TableCell sx={this.bodyCellSx("left")}>
                      {r.createdOn ? new Date(r.createdOn).toLocaleString("ro-RO") : ""}
                    </TableCell>

                    <TableCell sx={this.bodyCellSx("center")}>
                      <Chip size="small" variant="outlined" label={r.score ?? 0} sx={this.chipSx()} />
                    </TableCell>

                    <TableCell sx={this.bodyCellSx("left")}>
                      {r.order_location || "-"}
                    </TableCell>

                    <TableCell sx={this.bodyCellSx("left")}>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                          lineHeight: 1.35,
                        }}
                      >
                        {r.details || "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}

                {paged.length === 0 && !this.state.loading ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: "16px" }}>
                      <Typography variant="body2" sx={{ opacity: 0.75 }}>
                        No feedback found for selected filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, { value: -1, label: "All" }]}
          labelRowsPerPage="Rows:"
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={this.handleChangePage}
          onRowsPerPageChange={this.handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid rgba(0,0,0,0.08)",
            ".MuiTablePagination-toolbar": {
              minHeight: { xs: 46, sm: 52 },
              px: { xs: 1, sm: 2 },
            },
            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
              fontSize: { xs: "13px", sm: "13px" },
            },
            ".MuiTablePagination-actions button": {
              padding: { xs: "4px", sm: "8px" },
            },
          }}
        />
      </Box>
    );
  }

  render() {
    return (
      <Card
        sx={{
          borderRadius: "14px",
          overflow: "hidden",
          height: {
            xs: "calc(100vh - 92px)",
            md: "calc(100vh - 104px)",
          },
          minHeight: {
            xs: 500,
            sm: 540,
          },
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {this.renderHeader()}
        {this.renderFiltersPanel()}
        {this.renderCompactSummary()}
        {this.renderFeedbackTable()}

        {this.state.loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(255,255,255,0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 3,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Card>
    );
  }

  filterSx() {
    return {
      width: "100%",
      minWidth: 0,
      "& .MuiInputBase-input": {
        fontSize: {
          xs: "14px",
          sm: "14px",
        },
      },
      "& .MuiInputLabel-root": {
        fontSize: {
          xs: "13px",
          sm: "13px",
        },
      },
    };
  }

  filterLabelSx() {
    return {
      color: "#1a237e",
      fontSize: {
        xs: "11px",
        sm: "12px",
      },
      fontWeight: 800,
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      lineHeight: 1.2,
    };
  }

  filterValueSx() {
    return {
      color: "#1e2833",
      fontSize: {
        xs: "14px",
        sm: "14px",
      },
      fontWeight: 800,
      lineHeight: 1.3,
      mt: 0.25,
    };
  }

  chipSx() {
    return {
      borderRadius: "10px",
      height: {
        xs: 26,
        sm: 28,
      },
      "& .MuiChip-label": {
        fontSize: {
          xs: "12px",
          sm: "13px",
        },
      },
    };
  }

  headerCellSx(align, width = null) {
    return {
      p: {
        xs: "8px 6px",
        sm: "8px 7px",
      },
      textAlign: align,
      width: width,
      whiteSpace: "nowrap",
      fontSize: {
        xs: "13px",
        sm: "13px",
        md: "14px",
      },
      backgroundColor: "#f6f4ef",
    };
  }

  bodyCellSx(align) {
    return {
      p: {
        xs: "8px 6px",
        sm: "8px 7px",
      },
      textAlign: align,
      whiteSpace: align === "left" ? "normal" : "nowrap",
      fontSize: {
        xs: "13.5px",
        sm: "13px",
        md: "14px",
      },
      lineHeight: 1.35,
    };
  }

  compactFilterSx() {
    return {
      width: "100%",
      minWidth: 0,
      "& .MuiOutlinedInput-root": {
        height: 40,
        borderRadius: "10px",
        backgroundColor: "rgba(255,255,255,0.55)",
      },
      "& .MuiInputBase-input": {
        fontSize: {
          xs: "13.5px",
          sm: "14px",
        },
        py: 0.75,
      },
      "& .MuiInputLabel-root": {
        fontSize: {
          xs: "12.5px",
          sm: "13px",
        },
      },
    };
  }
}

export default OrderFeedbackView;
